import { MAPS, mapInfo, type MapId } from "@69ly/maps";
import { useRef, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import { Banner, ErrorState, Loading } from "../components/Status.tsx";
import { errorText } from "../lib/errors.ts";
import { useDeleteMesh, useMapMeshes, useUploadMesh } from "../lib/map-meshes.ts";
import { useMe } from "../lib/queries.ts";

const COMMAND = 'pnpm map-export --cli "<Source2Viewer-CLI>" --game "<…>/game/csgo/maps" --textured';

/** The map a file is for, from its name: "de_mirage.mesh.bin.gz", "de_mirage.textured.glb". */
const mapOfFile = (name: string): MapId | null => MAPS.find((m) => name.toLowerCase().startsWith(`${m.id}.`))?.id ?? null;
/** A .glb is the map as it looks; anything else its mesh. */
const kindOfFile = (name: string): "mesh" | "textured" => (name.toLowerCase().endsWith(".glb") ? "textured" : "mesh");

/** Admins only: the 3D viewer's map meshes, made with tools/map-export and uploaded here. */
export function MapMeshAdminPage() {
  const { t, i18n } = useTranslation();
  const me = useMe().data;
  const meshes = useMapMeshes();
  const upload = useUploadMesh();
  const remove = useDeleteMesh();
  const [busy, setBusy] = useState<{ map: string; kind: "mesh" | "textured" } | null>(null);
  const [messages, setMessages] = useState<{ tone: "success" | "warning"; text: string }[]>([]);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  if (!me?.isAdmin) return <Navigate to="/library" replace />;
  if (meshes.isPending) return <Loading />;
  if (meshes.error) return <ErrorState message={errorText(t, meshes.error)} onRetry={() => void meshes.refetch()} />;

  const date = (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" });
  const send = async (files: File[], only?: MapId, onlyKind?: "mesh" | "textured") => {
    const out: typeof messages = [];
    // Meshes first: a textured map needs its mesh.
    const sorted = [...files].sort((a, b) => (kindOfFile(a.name) === "mesh" ? 0 : 1) - (kindOfFile(b.name) === "mesh" ? 0 : 1));
    for (const file of sorted) {
      const map = only ?? mapOfFile(file.name);
      const kind = onlyKind ?? kindOfFile(file.name);
      if (!map) {
        out.push({ tone: "warning", text: t("meshAdmin.unknownFile", { name: file.name }) });
        continue;
      }
      setBusy({ map, kind });
      try {
        await upload.mutateAsync({ map, file, kind });
        out.push({ tone: "success", text: t(kind === "textured" ? "meshAdmin.uploadedTextured" : "meshAdmin.uploaded", { map: mapInfo(map).name }) });
      } catch (error) {
        out.push({ tone: "warning", text: `${mapInfo(map).name}: ${errorText(t, error)}` });
      }
    }
    setBusy(null);
    setMessages(out);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    void send([...e.dataTransfer.files]);
  };

  return (
    <div className="page mesh-admin">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t("meshAdmin.title")}</h1>
          <p className="muted">{t("meshAdmin.intro")}</p>
        </div>
      </div>

      <div
        className={`card mesh-drop${over ? " is-over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <p>
          <strong>{t("meshAdmin.drop")}</strong>
        </p>
        <p className="muted small">{t("meshAdmin.how")}</p>
        <code className="mono small mesh-command">{COMMAND}</code>
        <div className="row">
          <button type="button" className="btn btn-primary" onClick={() => input.current?.click()} disabled={busy !== null}>
            {t("meshAdmin.pick")}
          </button>
        </div>
        <input
          ref={input}
          type="file"
          accept=".gz,.glb"
          multiple
          hidden
          data-testid="mesh-files"
          onChange={(e) => {
            void send([...(e.target.files ?? [])]);
            e.target.value = "";
          }}
        />
      </div>

      {messages.map((m, i) => (
        <Banner key={i} tone={m.tone}>
          {m.text}
        </Banner>
      ))}

      <table className="mesh-table">
        <thead>
          <tr>
            <th>{t("meshAdmin.map")}</th>
            <th>{t("meshAdmin.status")}</th>
            <th>{t("meshAdmin.textured")}</th>
          </tr>
        </thead>
        <tbody>
          {MAPS.filter((m) => m.activeDuty).map((m) => {
            const mesh = meshes.data.find((x) => x.map === m.id);
            return (
              <tr key={m.id}>
                <th scope="row">{m.name}</th>
                <td>
                  {busy?.map === m.id && busy.kind === "mesh" ? (
                    <span className="muted">{t("meshAdmin.sending")}</span>
                  ) : mesh ? (
                    <>
                      <span>{t("meshAdmin.ready", { triangles: mesh.triangles.toLocaleString(i18n.language), mb: (mesh.bytes / 1e6).toFixed(1) })}</span>
                      <br />
                      <span className="muted small">
                        {t("meshAdmin.by", { date: date(mesh.uploadedAt), name: mesh.uploadedBy ?? "—" })}
                        {mesh.source && ` · ${t("meshAdmin.game", { date: date(mesh.source) })}`}
                      </span>
                    </>
                  ) : (
                    <span className="muted">{t("meshAdmin.none")}</span>
                  )}
                  <div className="mesh-actions">
                    <label className="btn btn-secondary btn-small">
                      {mesh ? t("meshAdmin.replace") : t("meshAdmin.upload")}
                      <input
                        type="file"
                        accept=".gz"
                        hidden
                        aria-label={t("meshAdmin.uploadFor", { map: m.name })}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void send([file], m.id, "mesh");
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {mesh && (
                      <button
                        type="button"
                        className="link-button"
                        disabled={remove.isPending}
                        aria-label={t("meshAdmin.removeOf", { map: m.name })}
                        onClick={() => {
                          if (window.confirm(t("meshAdmin.removeConfirm", { map: m.name }))) remove.mutate({ map: m.id });
                        }}
                      >
                        {t("meshAdmin.remove")}
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  {busy?.map === m.id && busy.kind === "textured" ? (
                    <span className="muted">{t("meshAdmin.sending")}</span>
                  ) : mesh?.textured ? (
                    <>
                      <span>{t("meshAdmin.texturedReady", { triangles: mesh.textured.triangles.toLocaleString(i18n.language), mb: (mesh.textured.bytes / 1e6).toFixed(1) })}</span>
                      <br />
                      <span className="muted small">{t("meshAdmin.uploadedOn", { date: date(mesh.textured.uploadedAt) })}</span>
                    </>
                  ) : (
                    <span className="muted">{mesh ? t("meshAdmin.texturedNone") : t("meshAdmin.texturedNeedsMesh")}</span>
                  )}
                  {mesh && (
                    <div className="mesh-actions">
                      <label className="btn btn-secondary btn-small">
                        {mesh.textured ? t("meshAdmin.replace") : t("meshAdmin.upload")}
                        <input
                          type="file"
                          accept=".glb"
                          hidden
                          aria-label={t("meshAdmin.uploadTexturedFor", { map: m.name })}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void send([file], m.id, "textured");
                            e.target.value = "";
                          }}
                        />
                      </label>
                      {mesh.textured && (
                        <button
                          type="button"
                          className="link-button"
                          disabled={remove.isPending}
                          aria-label={t("meshAdmin.removeTexturedOf", { map: m.name })}
                          onClick={() => {
                            if (window.confirm(t("meshAdmin.removeTexturedConfirm", { map: m.name }))) remove.mutate({ map: m.id, kind: "textured" });
                          }}
                        >
                          {t("meshAdmin.remove")}
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
